import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Leaf, MessageCircleHeart, ShieldCheck, FileDown } from "lucide-react";
import TreeMark, { TREES_BIRDS_URL } from "@/components/TreeMark";

const HERO_IMG = TREES_BIRDS_URL;
const PLAN_IMG = "https://customer-assets.emergentagent.com/job_grief-admin-guide/artifacts/1dmp0uw6_Picture1.jpg";

export default function Landing() {
  return (
    <div>
      {/* Banner strip — trees & birds illustration */}
      <div
        aria-hidden
        className="w-full banner-strip"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 pt-14 pb-16 md:pt-20 md:pb-24">
          <div className="max-w-3xl reveal" style={{ animationDelay: "80ms" }}>
            <div className="flex items-center gap-3 mb-6">
              <TreeMark size={48} tone="dark" />
              <div className="text-xs uppercase tracking-[0.25em] text-[#6B7F62]">A gentle companion · UK</div>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D332D] leading-[1.05] tracking-tight">
              When it happens, no one hands you a manual.
            </h1>
            <p className="mt-6 text-lg text-[#576056] leading-relaxed max-w-2xl">
              At The End of The Day is a warm, practical guide to end-of-life admin — for those preparing so their family isn't overwhelmed, and for those already carrying the weight of a loss. Take it one small step at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Two paths */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <Link
            to="/guide/bereaved"
            data-testid="path-bereaved"
            className="group card p-8 md:p-10 transition-all hover:border-[#9EAD94] hover:shadow-[0_8px_24px_rgba(45,51,45,0.06)] flex flex-col h-full"
          >
            <div className="w-14 h-14 rounded-full bg-[#F5ECE7] flex items-center justify-center mb-6">
              <HeartHandshake className="text-[#D1A793]" size={26} strokeWidth={1.5} />
            </div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-2">Path one</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2D332D] mb-3 leading-tight">
              I've been bereaved.
            </h2>
            <p className="text-[#576056] leading-relaxed mb-6">
              First, take a breath. This is a gentle map of what usually needs attention — organised so you can pick it up when you have strength, and put it down when you don't.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[#6B7F62] group-hover:gap-3 transition-all mt-auto">
              Where to start <ArrowRight size={17} />
            </span>
          </Link>

          <Link
            to="/guide/prepare"
            data-testid="path-prepare"
            className="group card p-8 md:p-10 transition-all hover:border-[#9EAD94] hover:shadow-[0_8px_24px_rgba(45,51,45,0.06)] flex flex-col h-full"
          >
            <div className="w-14 h-14 rounded-full bg-[#E9ECE8] flex items-center justify-center mb-6">
              <Leaf className="text-[#6B7F62]" size={26} strokeWidth={1.5} />
            </div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-2">Path two</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2D332D] mb-3 leading-tight">
              I want to prepare for my family.
            </h2>
            <p className="text-[#576056] leading-relaxed mb-6">
              Build a private plan — accounts, executors, wishes, letters. The kindest thing you can leave behind is clarity. Save it, export it, share it with someone you trust.
            </p>
            <span className="inline-flex items-center gap-1.5 text-[#6B7F62] group-hover:gap-3 transition-all mt-auto">
              Begin your plan <ArrowRight size={17} />
            </span>
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="bg-[#F3F1ED]/60 border-y border-[#EAE6E1]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-4">What's inside</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2D332D] leading-snug">
              A companion, not a checklist you have to conquer.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <ShieldCheck size={22} strokeWidth={1.5} />,
                title: "UK-focused guidance",
                text: "Tell Us Once, probate, HMRC, DWP, Cruse — everything referenced with real UK contact points.",
              },
              {
                icon: <MessageCircleHeart size={22} strokeWidth={1.5} />,
                title: "A compassionate assistant",
                text: "Ask anything — from 'what's a Grant of Probate?' to 'where do I even start today?' — and get a warm, practical answer.",
              },
              {
                icon: <FileDown size={22} strokeWidth={1.5} />,
                title: "Private plan, exportable",
                text: "Build your plan online, save it securely, and download a PDF to store with your will. Update whenever you like.",
              },
            ].map((f, i) => (
              <div key={i} className="reveal" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="w-11 h-11 rounded-full bg-white border border-[#EAE6E1] flex items-center justify-center text-[#6B7F62] mb-4">
                  {f.icon}
                </div>
                <h3 className="font-serif text-xl text-[#2D332D] mb-2">{f.title}</h3>
                <p className="text-[#576056] leading-relaxed text-[15px]">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plan CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-4">A gift of clarity</div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#2D332D] leading-tight mb-6">
              The kindest thing you can leave is a plan.
            </h2>
            <p className="text-[#576056] text-lg leading-relaxed mb-8 max-w-xl">
              Not a Google Doc buried in a drive. Not a pile of scattered paperwork to sift through. A calm, structured place where your family can find what they need — accounts, executors, funeral wishes, and the letters you always meant to write.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="btn-primary">
                Start your plan <ArrowRight size={18} />
              </Link>
              <Link to="/guide/prepare" data-testid="hero-cta-preview" className="btn-secondary">
                Read the guide first
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative">
              <div
                className="aspect-[4/5] w-full rounded-3xl bg-cover bg-center"
                style={{ backgroundImage: `url(${PLAN_IMG})` }}
              />
              <div className="mt-6 sm:mt-0 sm:absolute sm:-bottom-6 sm:-left-6 bg-white border border-[#EAE6E1] rounded-2xl px-5 py-4 shadow-[0_4px_16px_rgba(45,51,45,0.06)] sm:max-w-[240px]" data-testid="dedication-card">
                <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-1">Dedication</div>
                <div className="text-sm text-[#2D332D] leading-snug font-serif italic">In loving memory of my Dad.</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
